import type { Selector } from '@reduxjs/toolkit';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, AppState } from './store.ts';

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch = useDispatch.withTypes<AppDispatch>();
export const useAppSelector = useSelector.withTypes<AppState>();

export type SelectorChangeEffect<TValue> = (previousValue: TValue, newValue: TValue) => void;

export const useSelectorChange = <TValue>(
  selector: Selector<AppState, TValue>,
  effect: SelectorChangeEffect<TValue>,
) => {
  const currentValue = useAppSelector(selector);
  const previousValue = useRef<TValue>(currentValue);

  const onChange = useCallback(
    (previousValue: TValue, newValue: TValue) => {
      if (previousValue === newValue) {
        return;
      }

      effect(previousValue, newValue);
    },
    [effect],
  );

  useEffect(() => {
    onChange(previousValue.current, currentValue);

    previousValue.current = currentValue;
  }, [currentValue, onChange]);
};

export const usePreserveQueryParams = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const preserveQueryParamsAndNavigate = (to: { pathname: string; search?: string }) => {
    const currentParams = new URLSearchParams(location.search);
    const newParams = new URLSearchParams(to.search);

    // Combine current and new query parameters
    for (const [key, value] of currentParams) {
      newParams.set(key, value);
    }

    navigate({ to: `${to.pathname}?${newParams.toString()}` });
  };

  return { preserveQueryParamsAndNavigate };
};
